/**
 * Sektor-Rotations-Detektor.
 *
 * Vergleicht die relativen 26-Wochen-Returns der wichtigsten Sektor-ETFs
 * gegen SPY. Liefert eine Reihenfolge der Sektoren — die führenden Sektoren
 * deuten auf das aktuelle Markt-Narrativ hin (Tech-Lead = Risk-On,
 * Defensiv-Lead = Risk-Off, Energy-Lead = Inflation-Trade etc.).
 *
 * Quelle: Stan Weinstein "Secrets for Profiting in Bull and Bear Markets"
 * (Sektor-Rotation als Stage-Indikator), Sam Stovall "Standard & Poor's
 * Guide to Sector Investing" (Defensiv vs. Zyklisch).
 */

import type { PriceQuote } from '@/lib/market-data/types';

export interface SectorEtf {
  symbol: string;
  label: string;
  /** "cyclical" = Tech/Cons-Disc/Industrials, "defensive" = Cons-Stap/Util/Health,
   *  "rate_sensitive" = Financials/Real-Estate, "commodity" = Energy/Materials,
   *  "crypto" = Krypto-Anker (BTC/ETH) — Welle 8 */
  bucket: 'cyclical' | 'defensive' | 'rate_sensitive' | 'commodity' | 'crypto';
}

export const SECTOR_ETFS: SectorEtf[] = [
  { symbol: 'XLK', label: 'Tech', bucket: 'cyclical' },
  { symbol: 'XLY', label: 'Cons. Disc', bucket: 'cyclical' },
  { symbol: 'XLI', label: 'Industrials', bucket: 'cyclical' },
  { symbol: 'XLP', label: 'Cons. Staples', bucket: 'defensive' },
  { symbol: 'XLU', label: 'Utilities', bucket: 'defensive' },
  { symbol: 'XLV', label: 'Health', bucket: 'defensive' },
  { symbol: 'XLF', label: 'Financials', bucket: 'rate_sensitive' },
  { symbol: 'XLE', label: 'Energy', bucket: 'commodity' },
];

export interface SectorRanking {
  symbol: string;
  label: string;
  bucket: SectorEtf['bucket'];
  return_26w_pct: number | null;
  /** 26W-Return relativ zu SPY in Prozentpunkten (positive = Outperformer) */
  rs_vs_spy_pct: number | null;
}

export interface SectorRotationSnapshot {
  rankings: SectorRanking[];
  /** Welcher Bucket führt? */
  leading_bucket: SectorEtf['bucket'] | null;
  /** Klartext-Interpretation */
  narrative: string;
}

export function computeSectorRotation(
  spyQuote: PriceQuote | null,
  sectorQuotes: Map<string, PriceQuote | null>,
  /** Welle 8 — BTC-Quote optional, damit Krypto in der Rotation mitlaufen kann. */
  btcQuote?: PriceQuote | null
): SectorRotationSnapshot {
  const spyReturn = spyQuote?.return_26w_pct ?? null;

  const rankings: SectorRanking[] = SECTOR_ETFS.map((s) => {
    const q = sectorQuotes.get(s.symbol) ?? null;
    const ret = q?.return_26w_pct ?? null;
    const rs =
      ret !== null && spyReturn !== null ? ret - spyReturn : null;
    return {
      symbol: s.symbol,
      label: s.label,
      bucket: s.bucket,
      return_26w_pct: ret,
      rs_vs_spy_pct: rs,
    };
  });

  // Welle 8 — Krypto (BTC) als neunte Reihe.
  if (btcQuote) {
    const btcReturn = btcQuote.return_26w_pct ?? null;
    const btcRs = btcReturn !== null && spyReturn !== null ? btcReturn - spyReturn : null;
    rankings.push({
      symbol: 'BTC/USD',
      label: 'Krypto (BTC)',
      bucket: 'crypto',
      return_26w_pct: btcReturn,
      rs_vs_spy_pct: btcRs,
    });
  }

  rankings.sort((a, b) => {
    const av = a.rs_vs_spy_pct ?? -Infinity;
    const bv = b.rs_vs_spy_pct ?? -Infinity;
    return bv - av;
  });

  const top3 = rankings.filter((r) => r.rs_vs_spy_pct !== null).slice(0, 3);
  const bucketCount = new Map<SectorEtf['bucket'], number>();
  for (const r of top3) {
    bucketCount.set(r.bucket, (bucketCount.get(r.bucket) ?? 0) + 1);
  }

  let leadingBucket: SectorEtf['bucket'] | null = null;
  let maxCount = 0;
  for (const [bucket, count] of bucketCount) {
    if (count > maxCount) {
      maxCount = count;
      leadingBucket = bucket;
    }
  }

  let narrative = 'Keine eindeutige Sektor-Führerschaft.';
  if (leadingBucket && top3.length >= 2) {
    switch (leadingBucket) {
      case 'cyclical':
        narrative =
          'Zyklische Sektoren führen — Risk-On-Phase, Trendfolge unterstützt.';
        break;
      case 'defensive':
        narrative =
          'Defensive Sektoren führen — Risk-Off-Tendenz, Vorsicht bei Breakouts.';
        break;
      case 'rate_sensitive':
        narrative =
          'Zinssensitive Sektoren führen — Markt erwartet Zinsänderungen.';
        break;
      case 'commodity':
        narrative =
          'Rohstoff-Sektoren führen — Inflations- oder Geopolitik-Narrativ aktiv.';
        break;
      case 'crypto':
        narrative =
          'Krypto führt — Risk-On-Spitze, beachte Korrelation zu Tech.';
        break;
    }
  }

  return { rankings, leading_bucket: leadingBucket, narrative };
}
